import type { Measure, ModuleRequirement, Organization, VehicleType } from '@/state/types';

/**
 * Phase 9.1 — Maßnahmen-Definitionen.
 * Phase 10.3.2 — Cross-organisation gating via `organization`, `blockedBy`,
 * `blocksOtherMeasures`, `requiredVehicleTypes`, `minVehicleCount`.
 */

export type MeasureCategory = Measure['category'];

export interface MeasureTemplate {
  templateId: string;
  name: string;
  category: MeasureCategory;
  /** Game-time ms to complete this measure (when continuously fulfilled). */
  durationMs: number;
  requiredModules: Omit<ModuleRequirement, 'fulfilled'>[];
  organization?: Organization;
  requiredVehicleTypes?: VehicleType[];
  minVehicleCount?: number;
  blockedBy?: string[];
  blocksOtherMeasures?: string[];
}

/* ──────── Reusable templates ──────── */

const T_BRANDBEKAEMPFUNG_KLEIN: MeasureTemplate = {
  templateId: 'brandbekaempfung-klein',
  name: 'Brandbekämpfung',
  category: 'fire',
  organization: 'fire',
  durationMs: 5 * 60_000,
  requiredModules: [
    { family: 'water-tank' },
    { family: 'breathing-air', minSize: 'small' },
  ],
};

const T_BRANDBEKAEMPFUNG: MeasureTemplate = {
  templateId: 'brandbekaempfung',
  name: 'Brandbekämpfung',
  category: 'fire',
  organization: 'fire',
  durationMs: 8 * 60_000,
  requiredModules: [
    { family: 'water-tank', minSize: '1200l' },
    { family: 'breathing-air', minSize: 'small' },
  ],
};

const T_BRANDBEKAEMPFUNG_GROSS: MeasureTemplate = {
  templateId: 'brandbekaempfung-gross',
  name: 'Großbrandbekämpfung',
  category: 'fire',
  organization: 'fire',
  durationMs: 12 * 60_000,
  requiredModules: [
    { family: 'water-tank', minSize: '1800l' },
    { family: 'breathing-air', minSize: 'medium' },
    { family: 'water-monitor' },
  ],
};

const T_HOEHENRETTUNG: MeasureTemplate = {
  templateId: 'hoehenrettung',
  name: 'Höhenrettung (DLK)',
  category: 'rescue',
  organization: 'fire',
  durationMs: 4 * 60_000,
  requiredModules: [{ family: 'dlk-ladder' }],
};

const T_TECHNISCHE_RETTUNG: MeasureTemplate = {
  templateId: 'technische-rettung',
  name: 'Technische Rettung',
  category: 'technical',
  organization: 'fire',
  durationMs: 6 * 60_000,
  requiredModules: [{ family: 'hydraulic-rescue-set', minSize: 'small' }],
};

const T_TECHNISCHE_RETTUNG_SCHWER: MeasureTemplate = {
  templateId: 'technische-rettung-schwer',
  name: 'Schwere Technische Rettung',
  category: 'technical',
  organization: 'fire',
  durationMs: 10 * 60_000,
  requiredModules: [
    { family: 'hydraulic-rescue-set', minSize: 'large' },
    { family: 'technical-equipment' },
  ],
};

const T_PATIENTENVERSORGUNG: MeasureTemplate = {
  templateId: 'patientenversorgung',
  name: 'Patientenversorgung',
  category: 'medical',
  organization: 'ems',
  durationMs: 4 * 60_000,
  requiredModules: [{ family: 'emergency-doctor-equip' }],
};

const T_PATIENTENTRANSPORT: MeasureTemplate = {
  templateId: 'patiententransport',
  name: 'Patiententransport',
  category: 'transport',
  organization: 'ems',
  durationMs: 6 * 60_000,
  requiredModules: [{ family: 'emergency-doctor-equip' }],
};

const T_OELSPUR: MeasureTemplate = {
  templateId: 'oelspur-binden',
  name: 'Ölspur binden',
  category: 'technical',
  organization: 'fire',
  durationMs: 4 * 60_000,
  requiredModules: [{ family: 'environmental-protection' }],
};

const T_BMA_PRUEFEN: MeasureTemplate = {
  templateId: 'bma-pruefen',
  name: 'BMA prüfen',
  category: 'technical',
  organization: 'fire',
  durationMs: 3 * 60_000,
  requiredModules: [{ family: 'breathing-air', minSize: 'small' }],
};

const T_VEGETATIONSBRAND: MeasureTemplate = {
  templateId: 'vegetationsbrand',
  name: 'Vegetationsbrand-Bekämpfung',
  category: 'fire',
  organization: 'fire',
  durationMs: 20 * 60_000,
  requiredModules: [
    { family: 'water-tank', minSize: '1800l' },
    { family: 'foam-tank' },
    { family: 'all-wheel-drive' },
  ],
};

const T_SCHLAUCH_VERLEGEN: MeasureTemplate = {
  templateId: 'schlauch-verlegen',
  name: 'Wasserversorgung aufbauen',
  category: 'water-supply',
  organization: 'fire',
  durationMs: 6 * 60_000,
  requiredModules: [{ family: 'hose-reel' }],
};

const T_SCHWERLAST: MeasureTemplate = {
  templateId: 'schwerlast-bergung',
  name: 'Schwerlast-Bergung',
  category: 'rescue',
  organization: 'fire',
  durationMs: 12 * 60_000,
  requiredModules: [{ family: 'crane' }, { family: 'technical-equipment' }],
};

/* ──────── Phase 10.3 — Police measure templates ──────── */

const T_RUHESTOERUNG: MeasureTemplate = {
  templateId: 'ruhestoerung-klaeren',
  name: 'Streit klären',
  category: 'arrest',
  organization: 'police',
  durationMs: 3 * 60_000,
  requiredModules: [],
  requiredVehicleTypes: ['FuStW', 'GruKW'],
};

const T_VERHAFTUNG: MeasureTemplate = {
  templateId: 'verhaftung',
  name: 'Tatverdächtigen festnehmen',
  category: 'arrest',
  organization: 'police',
  durationMs: 4 * 60_000,
  requiredModules: [{ family: 'arrest-equipment' }],
};

const T_TATORT_SICHERN: MeasureTemplate = {
  templateId: 'tatort-sichern',
  name: 'Tatort absichern',
  category: 'arrest',
  organization: 'police',
  durationMs: 3 * 60_000,
  requiredModules: [{ family: 'arrest-equipment' }],
  // Phase 10.3.2 — While "Tatort sichern" is active, Patientenversorgung and
  // Patiententransport are blocked (Rettungsdienst kann erst rein, wenn Lage
  // gesichert ist).
  blocksOtherMeasures: ['patientenversorgung', 'patiententransport'],
};

const T_VERFOLGUNG: MeasureTemplate = {
  templateId: 'verfolgung',
  name: 'Verfolgung aufnehmen',
  category: 'arrest',
  organization: 'police',
  durationMs: 5 * 60_000,
  requiredModules: [],
  requiredVehicleTypes: ['FuStW', 'KradPol'],
  minVehicleCount: 2,
};

const T_ATEMTEST: MeasureTemplate = {
  templateId: 'atemtest',
  name: 'Atemalkohol-Test',
  category: 'investigation',
  organization: 'police',
  durationMs: 2 * 60_000,
  requiredModules: [],
  requiredVehicleTypes: ['FuStW'],
};

const T_VERKEHR_REGELN: MeasureTemplate = {
  templateId: 'verkehr-regeln',
  name: 'Verkehr regeln',
  category: 'crowd-control',
  organization: 'police',
  durationMs: 4 * 60_000,
  requiredModules: [],
  requiredVehicleTypes: ['FuStW', 'KradPol'],
};

const T_PERIMETER: MeasureTemplate = {
  templateId: 'perimeter',
  name: 'Perimeter absichern',
  category: 'crowd-control',
  organization: 'police',
  durationMs: 6 * 60_000,
  requiredModules: [{ family: 'arrest-equipment' }],
  minVehicleCount: 4,
  blocksOtherMeasures: ['patientenversorgung', 'patiententransport'],
};

const T_SEK_STUERMEN: MeasureTemplate = {
  templateId: 'sek-stuermen',
  name: 'Tatort stürmen',
  category: 'arrest',
  organization: 'police',
  durationMs: 10 * 60_000,
  requiredModules: [
    { family: 'breaching-equipment' },
    { family: 'tactical-gear', minSize: 'heavy' },
  ],
  requiredVehicleTypes: ['SEKWg'],
  blocksOtherMeasures: ['patientenversorgung', 'patiententransport'],
};

const T_VERHANDLUNG: MeasureTemplate = {
  templateId: 'verhandlung',
  name: 'Verhandlung',
  category: 'investigation',
  organization: 'police',
  durationMs: 15 * 60_000,
  requiredModules: [],
  requiredVehicleTypes: ['MEKWg'],
};

const T_LAGE_ERKUNDEN: MeasureTemplate = {
  templateId: 'lage-erkunden',
  name: 'Lage erkunden',
  category: 'investigation',
  organization: 'police',
  durationMs: 4 * 60_000,
  requiredModules: [],
  requiredVehicleTypes: ['FuStW', 'GruKW', 'KradPol'],
};

const T_DEMO_BEGLEITEN: MeasureTemplate = {
  templateId: 'demo-begleiten',
  name: 'Demo begleiten',
  category: 'crowd-control',
  organization: 'police',
  durationMs: 12 * 60_000,
  requiredModules: [],
  requiredVehicleTypes: ['GruKW'],
  minVehicleCount: 2,
};

const T_WASSERWERFER_EINSATZ: MeasureTemplate = {
  templateId: 'wasserwerfer-einsatz',
  name: 'Wasserwerfer-Einsatz',
  category: 'crowd-control',
  organization: 'police',
  durationMs: 10 * 60_000,
  requiredModules: [{ family: 'water-cannon' }],
  requiredVehicleTypes: ['WaWe'],
};

const T_SPRENGSTOFF: MeasureTemplate = {
  templateId: 'sprengstoff-pruefen',
  name: 'Sprengstoff prüfen',
  category: 'investigation',
  organization: 'police',
  durationMs: 12 * 60_000,
  requiredModules: [{ family: 'tactical-gear' }],
  requiredVehicleTypes: ['SEKWg'],
};

const T_BANK_SICHERN: MeasureTemplate = {
  templateId: 'bank-sichern',
  name: 'Bank absichern',
  category: 'arrest',
  organization: 'police',
  requiredModules: [{ family: 'arrest-equipment' }],
  durationMs: 8 * 60_000,
  minVehicleCount: 3,
};

const T_OPFER_VERSORGEN: MeasureTemplate = {
  templateId: 'opfer-versorgen',
  name: 'Opfer versorgen',
  category: 'medical',
  organization: 'ems',
  durationMs: 5 * 60_000,
  requiredModules: [{ family: 'emergency-doctor-equip' }],
  // Cross-org: blocked until "Tatort sichern" / "Perimeter" / "Stürmen" are done.
  blockedBy: ['tatort-sichern', 'perimeter', 'sek-stuermen'],
};

const T_GEISELN_VERSORGEN: MeasureTemplate = {
  templateId: 'geiseln-versorgen',
  name: 'Geiseln versorgen',
  category: 'medical',
  organization: 'ems',
  durationMs: 6 * 60_000,
  requiredModules: [{ family: 'emergency-doctor-equip' }],
  minVehicleCount: 2,
  blockedBy: ['sek-stuermen'],
};

/* ──────── Pro Mission-Code ──────── */

export const MEASURES_BY_CATALOG_ID: Record<string, MeasureTemplate[]> = {
  // Brände
  F1: [T_BRANDBEKAEMPFUNG_KLEIN],
  F2: [T_BRANDBEKAEMPFUNG, T_HOEHENRETTUNG],
  F3: [T_BRANDBEKAEMPFUNG_GROSS, T_HOEHENRETTUNG, T_PATIENTENVERSORGUNG],
  F4: [T_BRANDBEKAEMPFUNG_GROSS, T_HOEHENRETTUNG, T_SCHLAUCH_VERLEGEN, T_PATIENTENVERSORGUNG],
  F5: [T_VEGETATIONSBRAND, T_SCHLAUCH_VERLEGEN],
  // Hilfeleistung
  H1: [T_OELSPUR],
  H2: [T_TECHNISCHE_RETTUNG, T_PATIENTENVERSORGUNG],
  H3: [T_TECHNISCHE_RETTUNG_SCHWER, T_PATIENTENVERSORGUNG, T_PATIENTENTRANSPORT],
  H4: [T_BMA_PRUEFEN],
  // Rettungsdienst
  R1: [T_PATIENTENVERSORGUNG, T_PATIENTENTRANSPORT],
  R2: [T_PATIENTENVERSORGUNG, T_PATIENTENTRANSPORT],
  R3: [T_PATIENTENVERSORGUNG, T_PATIENTENTRANSPORT],
  R4: [T_PATIENTENVERSORGUNG, T_PATIENTENTRANSPORT, T_TECHNISCHE_RETTUNG],
  // Phase 9.6.5 — Schwerlastrettung mit FWK
  H5: [T_SCHWERLAST, T_PATIENTENVERSORGUNG, T_PATIENTENTRANSPORT],

  // Phase 10.3 — Polizei
  P0: [T_RUHESTOERUNG],
  P1: [T_RUHESTOERUNG],
  P2: [T_TATORT_SICHERN, T_VERHAFTUNG, T_OPFER_VERSORGEN, T_PATIENTENTRANSPORT],
  P3: [T_VERHAFTUNG, T_LAGE_ERKUNDEN],
  P4: [T_VERKEHR_REGELN, T_VERFOLGUNG, T_OPFER_VERSORGEN, T_PATIENTENTRANSPORT],
  P5: [T_ATEMTEST, T_VERHAFTUNG],
  P6: [T_PERIMETER, T_VERHAFTUNG],
  P7: [T_DEMO_BEGLEITEN, T_VERKEHR_REGELN],
  P8: [T_TATORT_SICHERN, T_VERHAFTUNG, T_OPFER_VERSORGEN, T_PATIENTENTRANSPORT],
  P9: [T_BANK_SICHERN, T_VERHAFTUNG, T_PERIMETER],
  P10: [T_LAGE_ERKUNDEN, T_PERIMETER, T_VERHANDLUNG, T_SEK_STUERMEN, T_GEISELN_VERSORGEN],
  P11: [T_PERIMETER, T_SEK_STUERMEN, T_OPFER_VERSORGEN, T_PATIENTENTRANSPORT],
  P12: [T_PERIMETER, T_SPRENGSTOFF],
  P13: [T_DEMO_BEGLEITEN, T_WASSERWERFER_EINSATZ, T_VERKEHR_REGELN],
};

/** Build a fresh `Measure[]` array for a freshly-spawned mission. */
export function makeMeasuresForCatalog(catalogId: string): Measure[] {
  const templates = MEASURES_BY_CATALOG_ID[catalogId] ?? [];
  return templates.map((t, idx) => ({
    id: `meas_${catalogId}_${idx}_${Math.random().toString(36).slice(2, 6)}`,
    templateId: t.templateId,
    name: t.name,
    category: t.category,
    organization: t.organization,
    status: 'pending',
    progress: 0,
    durationMs: t.durationMs,
    requiredModules: t.requiredModules.map((r) => ({ ...r, fulfilled: false })),
    requiredVehicleTypes: t.requiredVehicleTypes,
    minVehicleCount: t.minVehicleCount,
    blockedBy: t.blockedBy,
    blocksOtherMeasures: t.blocksOtherMeasures,
  }));
}
